import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import LoginModal from '../components/Mainpage/LoginModal';
import SignupModal from '../components/Mainpage/SignupModal';
import CartModal from '../components/Mainpage/CartModal';

const ShopUIContext = createContext(null);

/**
 * Owns the auth and cart modals for the whole app.
 *
 * Previously every product listing rendered its own <LoginModal> and
 * <CartModal>, and LoginModal in turn rendered SignupModal,
 * ForgetPassModel and GuestLoginModal — so the landing page alone
 * mounted that tree five times over. One instance is enough, and it
 * also means opening the cart from anywhere shows the same state.
 */
export const ShopUIProvider = ({ children }) => {
  const [loginState, setLoginState] = useState({ open: false, checkout: false });
  const [signupOpen, setSignupOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  const openLogin = useCallback((options = {}) => {
    setLoginState({ open: true, checkout: Boolean(options.checkout) });
  }, []);

  const closeLogin = useCallback(() => {
    setLoginState({ open: false, checkout: false });
  }, []);

  const openSignup = useCallback(() => setSignupOpen(true), []);
  const closeSignup = useCallback(() => setSignupOpen(false), []);
  const openCart = useCallback(() => setCartOpen(true), []);
  const closeCart = useCallback(() => setCartOpen(false), []);

  const value = useMemo(
    () => ({ openLogin, closeLogin, openSignup, closeSignup, openCart, closeCart }),
    [openLogin, closeLogin, openSignup, closeSignup, openCart, closeCart]
  );

  return (
    <ShopUIContext.Provider value={value}>
      {children}
      <LoginModal show={loginState.open} checkout={loginState.checkout} onClose={closeLogin} />
      <SignupModal show={signupOpen} onClose={closeSignup} />
      <CartModal show={cartOpen} onClose={closeCart} />
    </ShopUIContext.Provider>
  );
};

/**
 * Falls back to no-ops rather than throwing, so a component rendered
 * outside the provider (in a test, say) still mounts.
 */
export const useShopUI = () => {
  const context = useContext(ShopUIContext);
  return (
    context || {
      openLogin: () => {},
      closeLogin: () => {},
      openSignup: () => {},
      closeSignup: () => {},
      openCart: () => {},
      closeCart: () => {},
    }
  );
};

export default ShopUIContext;
